import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Packages  } from "assets/SideBarIcons/pakage.svg";
import { useGetPackageStationService, useAssignStationService, useUnassignStationService, useAssignAllStationService, useUnassignAllStationService } from 'subModule/src/services/packages/usePackages';
import PackageTabs from './PackageTabs';
import PackageStationSearchHeader from 'components/Packages/PackageStationSearchHeader';
import { SelectChangeEvent } from '@mui/material';
import { PackagesProfileService } from 'subModule/src/core/services';
import { GetPackageByIdResponse, PackagesResponse } from 'subModule/src/services/packages/interface';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';

interface SearchFormData {
    [key: string]: string | any;
}
export default function PackageStationsTable() {
    const params = useParams();
    const packageId = params?.id;
    const navigate = useNavigate();
    const [packageTitle, setPackageTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<string[]>(['name']);
    const [search, setSearch] = useState<any>({});
    const [packages, setPackages] = useState<PackagesResponse>({});
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ name: '', phone: '', email: '' });
    const { data: tableData, isFetching, refetch } = useGetPackageStationService({ pageNumber: pageNo, size: pageSize, ...search }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    let allStationsAssigned: unknown = tableData?.pages[0].data?.content.map(item => item.linked).every(linked => linked === true);
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        }).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        if(Object.keys(packages)?.length > 0) {
            fetchData();
        }
    }, [pageNo, pageSize, search, packages]);
    useEffect(() => {
        getByID();
    }, []);
    const getByID = () => {
        PackagesProfileService.getPackageById(+(params as any)?.id).then((res: GetPackageByIdResponse) => {
            if (res.success) {
                setPackages(res?.data);
                const name = pickProfileEntityTitle(res);
                if (name) setPackageTitle(name);
            } else {
                enqueueSnackbar(<AlertMessages text={res.message || "Something went wrong"} />, { variant: 'error' });
            }
        }).catch((err: any) => { console.error("===", err); });
    }
    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'packages', href: '/packages' },
            {
                labelKey: 'title',
                ...(packageTitle ? { label: packageTitle } : {}),
                href: packageId ? `/package/${packageId}` : undefined,
            },
            { labelKey: 'assignStation' },
        ],
        [packageId, packageTitle],
    );
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['name']);
        setSearchFormData({ name: '', phone: '', email: '' });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData =>
            Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : (typeof prevFormData[key] === 'string' ? '' : 0)]
            ))
        );
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = field === 'type' ? (value ? parseInt(value.value) || 0 : 0) : value;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
        }));
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) filteredSearch[key] = typeof value === 'string' ? value?.trim() : value;
        });
        if(Object.keys(filteredSearch).length > 0 && Object.values(filteredSearch).some(value => value)) {
            setSearch(filteredSearch);
            setPageNo(1);
            setPageSize(pageSize);
        }
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onAssignStation({ packageId: +(params as any).id, stationId: row.id });
        } else {
            onUnassignStation({ packageId: +(params as any).id, stationId: row.id });
        }
    }
    const assignUnassignAllEntities = (isChecked: boolean) => {
        setLoading(true);
        if (isChecked) {
            onAssignAllStations({ packageId: +(params as any).id });
        } else {
            onUnassignAllStations({ packageId: +(params as any).id });
        }
    }
    const { onAssignStation } = useAssignStationService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length === tableData?.pages[0].data.content.filter(station => station.linked).length) {
                    allStationsAssigned = true;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('station') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignStation } = useUnassignStationService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length !== tableData?.pages[0].data.content.filter(station => station.linked).length) {
                    allStationsAssigned = false;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('station') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onAssignAllStations } = useAssignAllStationService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                allStationsAssigned = true;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('stations') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignAllStations } = useUnassignAllStationService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                allStationsAssigned = false;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('stations') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/packages')}
                heading={t('packages')}
                icon={<Packages height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <PackageTabs params={params} selectedIndex={3} formData={packages} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'name', label: 'name', numeric: false },
                            { id: 'phoneNumber1', label: 'phoneNumber1', numeric: false },
                            { id: 'phoneNumber2', label: 'phoneNumber2', numeric: false },
                            { id: 'email', label: 'email', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data}
                        showCheckbox={true}
                        showActions={true}
                        enableSorting={true}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        assignUnassignSingleEntity={assignUnassignSingleEntity}
                        assignUnassignAllEntities={assignUnassignAllEntities}
                        allEntitiesAssigned={allStationsAssigned}
                        loadingSearch={isFetching || loading}
                        assignPrivilege={checkPrivileges('ROLE_PACKAGE_STATION_ASSIGN')}
                        unassignPrivilege={checkPrivileges('ROLE_PACKAGE_STATION_UNASSIGN')}
                        headerComponent={<PackageStationSearchHeader 
                            searchFormData={searchFormData}
                            handleChangeFilter={handleChangeFilter}
                            handleChangeSearch={handleChangeSearch}
                            handleSubmitSearch={handleSubmitSearch}
                            clearSearch={clearSearch}
                            search={search}
                            {...{filter}}
                        />}
                    />
                </div>
            </Box>
        </Box>
    )
}